import { z } from 'zod';

/**
 * Gardens and farms: crop beds, chicken coops and beehives that you tend a
 * little every day, like Harvest Moon.
 *
 * - A plot only grows on days it is watered (by you, once a day, or by rain).
 *   Miss a day and it wilts; three dry days in a row and it withers and dies.
 *   A dead crop stays in the bed until you clear it, and the seeds are lost.
 * - Every crop has seasons. Seasons last 10 real days; a crop that does not
 *   suit the next season dies when it arrives (ripe crops are spared, and
 *   greenhouse beds ignore seasons). Rainy days water outdoor beds for you.
 * - Animals (hens, bees) never die: they just do not produce if not fed.
 * - Harvests pay Kaki Dollars and garden XP. Garden levels unlock better
 *   seeds; beds, coops and hives are bought like any other furniture.
 * - Watering something every day builds a streak (with a day of grace), and
 *   streak milestones pay a bonus.
 *
 * The server is authoritative; these are the shared numbers and pure helpers.
 * Every WebSocket message here starts with `garden:`.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
/** Days roll over at midnight Singapore time, like everything else daily. */
const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;

/** The Singapore calendar day number of a timestamp. */
export function gardenDay(now: number): number {
  return Math.floor((now + SGT_OFFSET_MS) / DAY_MS);
}

/* -------------------------------------------------------------------------- */
/* Seasons and weather                                                        */
/* -------------------------------------------------------------------------- */

export const SEASONS = ['spring', 'summer', 'autumn', 'winter'] as const;
export type Season = (typeof SEASONS)[number];
/** Real days per season, so a year is 40 days. Everyone shares the same calendar. */
export const SEASON_DAYS = 10;

export const SEASON_LABEL: Readonly<Record<Season, string>> = Object.freeze({
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Monsoon Winter',
});

export function seasonOf(day: number): Season {
  return SEASONS[Math.floor(day / SEASON_DAYS) % SEASONS.length]!;
}

/** 1 to SEASON_DAYS. */
export function dayOfSeason(day: number): number {
  return (day % SEASON_DAYS) + 1;
}

export function seasonDaysLeft(day: number): number {
  return SEASON_DAYS - dayOfSeason(day);
}

const RAIN_CHANCE: Readonly<Record<Season, number>> = { spring: 0.2, summer: 0.15, autumn: 0.2, winter: 0.4 };

/** A stable 0..1 number for a day, so the weather is the same for everyone and can be forecast. */
function dayNoise(day: number): number {
  let h = Math.imul(day + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(day * 31 + 7, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Rainy days water every outdoor bed by themselves. */
export function isRainy(day: number): boolean {
  return dayNoise(day) < RAIN_CHANCE[seasonOf(day)];
}

/* -------------------------------------------------------------------------- */
/* Crops and animals                                                          */
/* -------------------------------------------------------------------------- */

export interface CropInfo {
  label: string;
  /** Plants are bought as seeds at planting time; animals need none. */
  kind: 'plant' | 'animal';
  /** Kaki Dollars per planting. */
  seedPrice: number;
  /** Watered days until it can be harvested. */
  growDays: number;
  /** Kaki Dollars for one harvest, before the bed's bonus. */
  sellPrice: number;
  /** Garden XP for one harvest. */
  xp: number;
  /** Garden level needed to plant it. */
  level: number;
  /** Watered days until the next harvest, or null if the plot empties after harvest. */
  regrowDays: number | null;
  /** Seasons it can be planted in. It dies when the season ends (unless it is ripe, or in a greenhouse). */
  seasons: readonly Season[];
  /** Colours for the sprite: leaves/stalk, then the fruit or flower. */
  leaf: string;
  fruit: string;
  /** How the sprite is drawn (see the client's gardenArt). */
  shape: 'leafy' | 'bush' | 'flower' | 'tall' | 'vine' | 'orchid' | 'tree' | 'lotus' | 'root' | 'gourd' | 'grain' | 'berry' | 'mushroom' | 'animal';
  description: string;
}

const ALL: readonly Season[] = SEASONS;
const SPRING = ['spring'] as const;
const SUMMER = ['summer'] as const;

export const CROPS = {
  kangkong: {
    label: 'Kangkong', kind: 'plant', seedPrice: 15, growDays: 2, sellPrice: 40, xp: 4, level: 1, regrowDays: null,
    seasons: ['spring', 'summer', 'autumn'], leaf: '#3d9e4c', fruit: '#7fd06a', shape: 'leafy', description: 'Water spinach. Fast and easy: perfect for a first crop.',
  },
  bayam: {
    label: 'Bayam', kind: 'plant', seedPrice: 15, growDays: 2, sellPrice: 42, xp: 4, level: 1, regrowDays: null,
    seasons: ['spring', 'autumn', 'winter'], leaf: '#3d9e4c', fruit: '#d9433b', shape: 'leafy', description: 'Red-stemmed spinach that likes cooler weather.',
  },
  radish: {
    label: 'Radish', kind: 'plant', seedPrice: 20, growDays: 3, sellPrice: 60, xp: 5, level: 1, regrowDays: null,
    seasons: ['spring', 'autumn', 'winter'], leaf: '#4aa858', fruit: '#f4e8ee', shape: 'root', description: 'Crunchy white radish for soup.',
  },
  chilli: {
    label: 'Chilli Padi', kind: 'plant', seedPrice: 25, growDays: 3, sellPrice: 80, xp: 6, level: 1, regrowDays: null,
    seasons: ['spring', 'summer'], leaf: '#2f7f3c', fruit: '#e8402a', shape: 'bush', description: 'Small, red and very hot.',
  },
  sunflower: {
    label: 'Sunflower', kind: 'plant', seedPrice: 25, growDays: 3, sellPrice: 75, xp: 6, level: 1, regrowDays: null,
    seasons: ['summer', 'autumn'], leaf: '#3d9e4c', fruit: '#ffd24a', shape: 'tall', description: 'Follows the sun and brightens any garden.',
  },
  kailan: {
    label: 'Kailan', kind: 'plant', seedPrice: 30, growDays: 3, sellPrice: 95, xp: 7, level: 2, regrowDays: null,
    seasons: ['spring', 'winter'], leaf: '#2f8f4c', fruit: '#e8f0d0', shape: 'leafy', description: 'Chinese kale with thick, crisp stems.',
  },
  carrot: {
    label: 'Carrot', kind: 'plant', seedPrice: 35, growDays: 4, sellPrice: 120, xp: 8, level: 2, regrowDays: null,
    seasons: ['spring', 'autumn'], leaf: '#4aa858', fruit: '#f08a2a', shape: 'root', description: 'Sweet orange carrots.',
  },
  tomato: {
    label: 'Tomato', kind: 'plant', seedPrice: 40, growDays: 4, sellPrice: 90, xp: 8, level: 2, regrowDays: 3,
    seasons: ['spring', 'summer'], leaf: '#3d9e4c', fruit: '#e8503a', shape: 'vine', description: 'Keeps fruiting: harvest again every 3 watered days.',
  },
  brinjal: {
    label: 'Brinjal', kind: 'plant', seedPrice: 45, growDays: 4, sellPrice: 150, xp: 9, level: 2, regrowDays: null,
    seasons: ['summer', 'autumn'], leaf: '#2f7f3c', fruit: '#6a3a9a', shape: 'bush', description: 'Glossy purple aubergines for sambal.',
  },
  strawberry: {
    label: 'Strawberry', kind: 'plant', seedPrice: 60, growDays: 5, sellPrice: 110, xp: 10, level: 3, regrowDays: 3,
    seasons: SPRING, leaf: '#3d9e4c', fruit: '#e8304a', shape: 'berry', description: 'Sweet spring berries. Fruits again every 3 watered days.',
  },
  corn: {
    label: 'Sweet Corn', kind: 'plant', seedPrice: 55, growDays: 5, sellPrice: 200, xp: 11, level: 3, regrowDays: null,
    seasons: ['summer', 'autumn'], leaf: '#4aa858', fruit: '#ffd84a', shape: 'grain', description: 'Tall stalks with golden cobs.',
  },
  cabbage: {
    label: 'Cabbage', kind: 'plant', seedPrice: 50, growDays: 5, sellPrice: 190, xp: 11, level: 3, regrowDays: null,
    seasons: ['autumn', 'winter'], leaf: '#5cbd63', fruit: '#a8e08a', shape: 'leafy', description: 'Heavy round heads that love cool, wet weather.',
  },
  pumpkin: {
    label: 'Pumpkin', kind: 'plant', seedPrice: 70, growDays: 6, sellPrice: 280, xp: 13, level: 3, regrowDays: null,
    seasons: ['autumn'], leaf: '#3d9e4c', fruit: '#f08a2a', shape: 'gourd', description: 'A big orange autumn pumpkin.',
  },
  pandan: {
    label: 'Pandan', kind: 'plant', seedPrice: 60, growDays: 5, sellPrice: 240, xp: 12, level: 3, regrowDays: null,
    seasons: ALL, leaf: '#4aa858', fruit: '#8ee07a', shape: 'leafy', description: 'Fragrant leaves for every kueh. Grows in any season.',
  },
  rose: {
    label: 'Rose', kind: 'plant', seedPrice: 60, growDays: 5, sellPrice: 230, xp: 12, level: 3, regrowDays: null,
    seasons: ['spring', 'summer'], leaf: '#2f7f3c', fruit: '#e8406a', shape: 'flower', description: 'Red roses, thorns and all.',
  },
  ginger: {
    label: 'Ginger', kind: 'plant', seedPrice: 80, growDays: 6, sellPrice: 330, xp: 14, level: 4, regrowDays: null,
    seasons: ['summer', 'autumn'], leaf: '#4aa858', fruit: '#d8b070', shape: 'root', description: 'Spicy roots for chicken rice.',
  },
  mushroom: {
    label: 'Oyster Mushroom', kind: 'plant', seedPrice: 70, growDays: 5, sellPrice: 260, xp: 13, level: 4, regrowDays: null,
    seasons: ['autumn', 'winter'], leaf: '#b8a890', fruit: '#efe6d8', shape: 'mushroom', description: 'Grows best when it is damp and cool.',
  },
  chrysanthemum: {
    label: 'Chrysanthemum', kind: 'plant', seedPrice: 70, growDays: 5, sellPrice: 270, xp: 13, level: 4, regrowDays: null,
    seasons: ['autumn', 'winter'], leaf: '#3d9e4c', fruit: '#ffd24a', shape: 'flower', description: 'Golden blooms for tea.',
  },
  lemongrass: {
    label: 'Lemongrass', kind: 'plant', seedPrice: 80, growDays: 6, sellPrice: 300, xp: 14, level: 4, regrowDays: null,
    seasons: ALL, leaf: '#8ec86a', fruit: '#d8e89a', shape: 'tall', description: 'Zesty stalks for tom yum. Grows in any season.',
  },
  sweet_potato: {
    label: 'Sweet Potato', kind: 'plant', seedPrice: 90, growDays: 6, sellPrice: 340, xp: 15, level: 4, regrowDays: null,
    seasons: SUMMER, leaf: '#3d9e4c', fruit: '#a04a9a', shape: 'root', description: 'Purple-skinned sweet potatoes. A summer crop.',
  },
  orchid: {
    label: 'Vanda Miss Joaquim', kind: 'plant', seedPrice: 100, growDays: 6, sellPrice: 420, xp: 16, level: 5, regrowDays: null,
    seasons: ALL, leaf: '#3d9e4c', fruit: '#c05ac8', shape: 'orchid', description: "Singapore's national flower. Grows in any season.",
  },
  watermelon: {
    label: 'Watermelon', kind: 'plant', seedPrice: 150, growDays: 6, sellPrice: 600, xp: 18, level: 5, regrowDays: null,
    seasons: SUMMER, leaf: '#2f8f4c', fruit: '#3f9a4c', shape: 'gourd', description: 'Big, striped and dripping with juice.',
  },
  dragonfruit: {
    label: 'Dragon Fruit', kind: 'plant', seedPrice: 180, growDays: 8, sellPrice: 420, xp: 20, level: 6, regrowDays: 5,
    seasons: ALL, leaf: '#3f9a5a', fruit: '#e8407a', shape: 'vine', description: 'Pink and spiky. Fruits again every 5 watered days.',
  },
  banana: {
    label: 'Banana', kind: 'plant', seedPrice: 250, growDays: 9, sellPrice: 700, xp: 24, level: 6, regrowDays: null,
    seasons: ALL, leaf: '#4aa858', fruit: '#ffd84a', shape: 'tree', description: 'A whole bunch, ripe and yellow.',
  },
  durian: {
    label: 'Durian', kind: 'plant', seedPrice: 400, growDays: 12, sellPrice: 900, xp: 30, level: 7, regrowDays: 9,
    seasons: ALL, leaf: '#2f7f3c', fruit: '#b5c84a', shape: 'tree', description: 'The king of fruits. Fruits again every 9 watered days.',
  },
  golden_lotus: {
    label: 'Golden Lotus', kind: 'plant', seedPrice: 800, growDays: 14, sellPrice: 3000, xp: 40, level: 9, regrowDays: null,
    seasons: ALL, leaf: '#3d9e8c', fruit: '#ffd24a', shape: 'lotus', description: 'A legendary flower that blooms once in a great while.',
  },
  eggs: {
    label: 'Fresh Eggs', kind: 'animal', seedPrice: 0, growDays: 1, sellPrice: 35, xp: 3, level: 1, regrowDays: 1,
    seasons: ALL, leaf: '#f4f0e4', fruit: '#f4f0e4', shape: 'animal', description: 'Feed the hens each day for a new batch of eggs.',
  },
  honey: {
    label: 'Wild Honey', kind: 'animal', seedPrice: 0, growDays: 3, sellPrice: 130, xp: 10, level: 1, regrowDays: 3,
    seasons: ALL, leaf: '#f0c850', fruit: '#e8b83a', shape: 'animal', description: 'Feed the bees and collect honey every 3 days.',
  },
} as const satisfies Record<string, CropInfo>;

export type CropId = keyof typeof CROPS;
export const CROP_IDS = Object.keys(CROPS) as CropId[];
export const PLANT_IDS = CROP_IDS.filter((id) => CROPS[id].kind === 'plant');

export function isCropId(id: string): id is CropId {
  return Object.prototype.hasOwnProperty.call(CROPS, id);
}

export function cropInfo(id: string): CropInfo | null {
  return isCropId(id) ? CROPS[id] : null;
}

/** Whether a crop may be planted (and survive) in a season. */
export function inSeason(crop: CropId, season: Season): boolean {
  return (CROPS[crop].seasons as readonly Season[]).includes(season);
}

/* -------------------------------------------------------------------------- */
/* Beds, coops and hives                                                      */
/* -------------------------------------------------------------------------- */

export interface GardenPlotInfo {
  /** Multiplies what a harvest sells for. */
  sellBonus: number;
  /** Animal homes always hold this one product; plant beds let you choose. */
  fixedCrop: CropId | null;
}

/** By furniture kind; only these kinds are garden plots. */
export const GARDEN_PLOTS: Readonly<Record<string, GardenPlotInfo>> = Object.freeze({
  garden_plot: { sellBonus: 1, fixedCrop: null },
  raised_bed: { sellBonus: 1.25, fixedCrop: null },
  greenhouse_bed: { sellBonus: 1.5, fixedCrop: null },
  chicken_coop: { sellBonus: 1, fixedCrop: 'eggs' },
  beehive: { sellBonus: 1, fixedCrop: 'honey' },
});

export function gardenPlotInfo(kind: string): GardenPlotInfo | null {
  return Object.prototype.hasOwnProperty.call(GARDEN_PLOTS, kind) ? GARDEN_PLOTS[kind]! : null;
}

/* -------------------------------------------------------------------------- */
/* Growth                                                                     */
/* -------------------------------------------------------------------------- */

export const GARDEN_STAGES = ['seed', 'sprout', 'growing', 'ready'] as const;
export type GardenStage = (typeof GARDEN_STAGES)[number];

/** Missed (unwatered, dry) days in a row after which a crop withers. One missed day only makes it wilt. */
export const WITHER_AFTER_MISSED_DAYS = 3;

export type WitherReason = 'thirst' | 'season';

export interface PlotSettleInput {
  crop: CropId;
  growth: number;
  /** Singapore day of the last watering, or 0. */
  wateredDay: number;
  /** Singapore day it was planted. */
  plantedDay: number;
  /** Greenhouse beds ignore seasons (and rain: they are indoors). */
  greenhouse: boolean;
}

export interface PlotSettled {
  growth: number;
  /** Last day it was actually watered or rained on (0 if never); planting does not count. */
  wateredDay: number;
  /** Unwatered days in a row before today (0 when watered or rained on yesterday). */
  missed: number;
  withered: WitherReason | null;
  /** Rain fell today, so it counts as watered. */
  rainedToday: boolean;
}

/**
 * Catches a plot up to `today`: rainy days water outdoor beds by themselves,
 * dry unwatered days pile up until the crop withers, and a season that does
 * not suit the crop kills it (unless it is already ripe, or in a greenhouse).
 * Pure and deterministic, so the server can replay the days it was not
 * looking, and the client can forecast the same result.
 */
export function settlePlot(input: PlotSettleInput, today: number): PlotSettled {
  const info = CROPS[input.crop];
  let growth = input.growth;
  // Days count from the last watering, or from planting.
  let care = Math.max(input.wateredDay, input.plantedDay);
  let watered = input.wateredDay;
  let missed = 0;
  let withered: WitherReason | null = null;
  const ripe = () => growth >= info.growDays;
  const outdoors = !input.greenhouse;

  // A wrong-season crop dies the day the season changes on it; ripe crops are spared.
  const seasonKills = (day: number) => outdoors && !ripe() && !inSeason(input.crop, seasonOf(day));

  for (let day = care + 1; day < today && !ripe(); day += 1) {
    if (seasonKills(day)) {
      withered = 'season';
      break;
    }
    if (outdoors && isRainy(day)) {
      growth = Math.min(info.growDays, growth + 1);
      care = day;
      watered = day;
      missed = 0;
    } else {
      missed += 1;
      if (missed >= WITHER_AFTER_MISSED_DAYS) {
        withered = 'thirst';
        break;
      }
    }
  }

  if (!withered && seasonKills(today)) withered = 'season';
  let rainedToday = false;
  if (!withered && !ripe() && outdoors && isRainy(today) && care < today) {
    growth = Math.min(info.growDays, growth + 1);
    care = today;
    watered = today;
    rainedToday = true;
    missed = 0;
  }
  return { growth, wateredDay: watered, missed, withered, rainedToday };
}

/** How far along a plot is, for the picture. `growth` is the number of watered days so far. */
export function gardenStage(crop: string, growth: number): GardenStage {
  const info = cropInfo(crop);
  if (!info) return 'seed';
  if (growth >= info.growDays) return 'ready';
  if (growth <= 0) return 'seed';
  return growth / info.growDays < 0.5 ? 'sprout' : 'growing';
}

/** Kaki Dollars a harvest pays on a bed of this kind. */
export function harvestValue(crop: CropId, plotKind: string): number {
  const bonus = gardenPlotInfo(plotKind)?.sellBonus ?? 1;
  return Math.round(CROPS[crop].sellPrice * bonus);
}

/** Watered days a harvest leaves behind: regrowing crops start over partway. */
export function growthAfterHarvest(crop: CropId): number | null {
  const info = CROPS[crop];
  return info.regrowDays === null ? null : Math.max(0, info.growDays - info.regrowDays);
}

/* -------------------------------------------------------------------------- */
/* Levels and streaks                                                         */
/* -------------------------------------------------------------------------- */

/** Garden XP needed for each level (index 0 = level 1). */
export const GARDEN_LEVEL_XP = [0, 20, 60, 130, 240, 400, 650, 1000, 1500, 2200] as const;
export const GARDEN_MAX_LEVEL = GARDEN_LEVEL_XP.length;

export function gardenLevel(xp: number): number {
  let level = 1;
  for (let i = 0; i < GARDEN_LEVEL_XP.length; i += 1) if (xp >= GARDEN_LEVEL_XP[i]!) level = i + 1;
  return level;
}

export function gardenNextLevelXp(level: number): number | null {
  return level >= GARDEN_MAX_LEVEL ? null : GARDEN_LEVEL_XP[level]!;
}

/** Watering a plot for the first time that day is worth this much garden XP. */
export const WATER_XP = 1;

/** Kaki Dollars for the first watering of the day: a little more for a longer streak. */
export function dailyWaterBonus(streak: number): number {
  return 10 + 5 * Math.min(8, Math.max(0, streak - 1));
}

/** Streak milestones and what they pay. */
export const STREAK_REWARDS: Readonly<Record<number, number>> = Object.freeze({
  3: 60,
  7: 200,
  14: 500,
  30: 1500,
  60: 3000,
  100: 6000,
});

/**
 * The streak after watering on `today`. Watering on consecutive days extends
 * it; missing exactly one day is forgiven (it carries on), while missing more
 * starts a new streak at 1. Watering twice in one day changes nothing.
 */
export function nextStreak(streak: number, lastWaterDay: number, today: number): number {
  if (streak > 0 && lastWaterDay === today) return streak;
  if (streak > 0 && today - lastWaterDay <= 2) return streak + 1;
  return 1;
}

/** Whether the streak is still alive (and so worth protecting) on `today`. */
export function streakAlive(streak: number, lastWaterDay: number, today: number): boolean {
  return streak > 0 && today - lastWaterDay <= 2;
}

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                */
/* -------------------------------------------------------------------------- */

export const gardenPlotViewSchema = z.object({
  itemId: z.string(),
  /** Why it died, if it did. It stays in the bed until cleared. */
  withered: z.enum(['thirst', 'season']).nullable(),
  /** Missed a day: water it today or it is closer to withering. */
  wilting: z.boolean(),
  /** Empty beds have no crop. */
  crop: z.string().nullable(),
  /** Watered days so far. */
  growth: z.number().int(),
  stage: z.enum(GARDEN_STAGES),
  /** Watered already today (Singapore time). */
  watered: z.boolean(),
  ready: z.boolean(),
});
export type GardenPlotView = z.infer<typeof gardenPlotViewSchema>;

export const gardenPlayerViewSchema = z.object({
  xp: z.number().int(),
  level: z.number().int(),
  nextLevelXp: z.number().int().nullable(),
  streak: z.number().int(),
  bestStreak: z.number().int(),
  /** Streak days still to go for the next bonus, if any. */
  nextReward: z.object({ days: z.number().int(), money: z.number().int() }).nullable(),
  /** Watered at least one plot today. */
  wateredToday: z.boolean(),
  harvests: z.number().int(),
  /** The shared garden calendar and weather. */
  season: z.enum(SEASONS),
  dayOfSeason: z.number().int(),
  seasonDaysLeft: z.number().int(),
  raining: z.boolean(),
  rainTomorrow: z.boolean(),
});
export type GardenPlayerView = z.infer<typeof gardenPlayerViewSchema>;

export const gardenServerMessageSchema = z.discriminatedUnion('t', [
  /** Every plot in a room, sent to everyone in it whenever one changes (and to a player joining). */
  z.object({
    t: z.literal('garden:plots'),
    roomId: z.string(),
    plots: z.array(gardenPlotViewSchema),
  }),
]);

export const gardenClientMessageSchema = z.discriminatedUnion('t', [
  /** Ask for the plots of the room you are in (answered with garden:plots). */
  z.object({ t: z.literal('garden:sync') }),
]);

export type GardenServerMessage = z.infer<typeof gardenServerMessageSchema>;
export type GardenClientMessage = z.infer<typeof gardenClientMessageSchema>;

export interface GardenActionResult {
  ok: true;
  /** Kaki Dollars after the action. */
  money: number;
  /** Kaki Dollars earned (or spent, negative) by the action itself. */
  delta: number;
  /** Bonus messages to show: streak rewards, level ups. */
  notes: string[];
  player: GardenPlayerView;
  plots: GardenPlotView[];
}
